import { useEffect, useMemo, useRef, useState } from "react";
import { fetchMailMessageAttachmentFlags } from "@/lib/api/imap";
import {
  mergeAttachmentFlagsCache,
  readAttachmentFlagsCache,
} from "@/lib/cache/attachmentFlagsCache";
import type { MailMessageSummary } from "@/lib/types/mail";

export type MultiAccountAttachmentMessage = MailMessageSummary & {
  accountId?: number;
  imapFolder?: string;
};

function attachmentRowKey(accountId: number, uid: number): string {
  return `${accountId}:${uid}`;
}

function groupKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

function flagsFromMessages(messages: MultiAccountAttachmentMessage[]): Map<string, boolean> {
  const map = new Map<string, boolean>();
  for (const msg of messages) {
    if (msg.uid <= 0 || msg.accountId == null || msg.hasAttachments === undefined) continue;
    map.set(attachmentRowKey(msg.accountId, msg.uid), msg.hasAttachments === true);
  }
  return map;
}

function mergeFlagMaps(...maps: Map<string, boolean>[]): Map<string, boolean> {
  const next = new Map<string, boolean>();
  for (const map of maps) {
    for (const [key, value] of map) {
      next.set(key, value);
    }
  }
  return next;
}

function messageHasAttachments(
  msg: MultiAccountAttachmentMessage,
  flagsByKey: Map<string, boolean>
): boolean {
  if (msg.hasAttachments === true) return true;
  if (msg.hasAttachments === false) return false;
  if (msg.accountId == null || msg.uid <= 0) return false;
  return flagsByKey.get(attachmentRowKey(msg.accountId, msg.uid)) === true;
}

/**
 * Icône trombone — réception unifiée (plusieurs comptes / UIDs dupliqués).
 */
export function useMultiAccountAttachmentFlags(
  messages: MultiAccountAttachmentMessage[]
): MultiAccountAttachmentMessage[] {
  const [fetchedFlags, setFetchedFlags] = useState<Map<string, boolean>>(() => new Map());
  const fetchedKeysRef = useRef<Set<string>>(new Set());

  const groups = useMemo(() => {
    const map = new Map<
      string,
      { accountId: number; folder: string; uids: number[]; listKey: string }
    >();
    for (const msg of messages) {
      if (msg.uid <= 0 || msg.accountId == null) continue;
      const folder = msg.imapFolder ?? "INBOX";
      const gKey = groupKey(msg.accountId, folder);
      const existing = map.get(gKey);
      if (existing) {
        existing.uids.push(msg.uid);
      } else {
        map.set(gKey, {
          accountId: msg.accountId,
          folder,
          uids: [msg.uid],
          listKey: "",
        });
      }
    }
    for (const group of map.values()) {
      group.listKey = [...new Set(group.uids)].sort((a, b) => a - b).join(",");
    }
    return [...map.values()];
  }, [messages]);

  const groupsKey = useMemo(
    () => groups.map((g) => `${g.accountId}\0${g.folder}\0${g.listKey}`).join("|"),
    [groups]
  );

  const storedFlags = useMemo(() => {
    const merged = new Map<string, boolean>();
    for (const group of groups) {
      const local = readAttachmentFlagsCache(group.accountId, group.folder);
      for (const [uid, value] of local) {
        merged.set(attachmentRowKey(group.accountId, uid), value);
      }
    }
    return merged;
  }, [groupsKey, groups]);

  const knownFromList = useMemo(() => flagsFromMessages(messages), [messages]);

  const effectiveFlags = useMemo(
    () => mergeFlagMaps(storedFlags, fetchedFlags, knownFromList),
    [storedFlags, fetchedFlags, knownFromList]
  );

  useEffect(() => {
    fetchedKeysRef.current = new Set();
    setFetchedFlags(new Map());
  }, [groupsKey]);

  useEffect(() => {
    for (const group of groups) {
      if (group.uids.length === 0) continue;
      const known = flagsFromMessages(
        messages.filter((m) => m.accountId === group.accountId && (m.imapFolder ?? "INBOX") === group.folder)
      );
      if (known.size === 0) continue;
      const byUid = new Map<number, boolean>();
      for (const [key, value] of known) {
        const uid = Number(key.split(":")[1]);
        if (uid > 0) byUid.set(uid, value);
      }
      mergeAttachmentFlagsCache(group.accountId, group.folder, byUid);
    }
  }, [groups, groupsKey, messages]);

  useEffect(() => {
    if (groups.length === 0) return;

    let cancelled = false;

    void (async () => {
      for (const group of groups) {
        if (cancelled) break;

        const uidsToFetch = [...new Set(group.uids)].filter((uid) => {
          const rowKey = attachmentRowKey(group.accountId, uid);
          const fetchKey = `${group.accountId}\0${group.folder}\0${uid}`;
          if (fetchedKeysRef.current.has(fetchKey)) return false;
          if (effectiveFlags.has(rowKey)) return false;
          return true;
        });

        if (uidsToFetch.length === 0) continue;

        const res = await fetchMailMessageAttachmentFlags({
          accountId: group.accountId,
          folder: group.folder,
          uids: uidsToFetch,
        });
        if (cancelled || !res.ok) continue;

        for (const uid of uidsToFetch) {
          fetchedKeysRef.current.add(`${group.accountId}\0${group.folder}\0${uid}`);
        }

        const fetched = new Map<string, boolean>();
        const byUid = new Map<number, boolean>();
        for (const item of res.data.flags ?? []) {
          if (item.uid <= 0) continue;
          fetched.set(attachmentRowKey(group.accountId, item.uid), item.hasAttachments);
          byUid.set(item.uid, item.hasAttachments);
        }
        if (fetched.size === 0) continue;

        mergeAttachmentFlagsCache(group.accountId, group.folder, byUid);
        setFetchedFlags((prev) => mergeFlagMaps(prev, fetched));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveFlags, groups, groupsKey]);

  return useMemo(
    () =>
      messages.map((msg) => {
        if (msg.uid <= 0) {
          return { ...msg, hasAttachments: false };
        }
        return {
          ...msg,
          hasAttachments: messageHasAttachments(msg, effectiveFlags),
        };
      }),
    [messages, effectiveFlags]
  );
}
